import { useState, useCallback, useEffect, type RefObject } from 'react';
import type { BannerNFTElement } from '../types';

interface UseBannerInteractionOptions {
  canvasRef: RefObject<HTMLCanvasElement>;
  elements: BannerNFTElement[];
  selectedElementId: string | null;
  canvasWidth: number;
  canvasHeight: number;
  displayScale: number;
  onSelectElement: (elementId: string | null) => void;
  onUpdatePosition: (elementId: string, position: { x: number; y: number }) => void;
  onUpdateScale: (elementId: string, scale: number) => void;
  onBringToFront: (elementId: string) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onResizeStart: (handle: string) => void;
  onResizeEnd: () => void;
  loadedImages: Map<string, HTMLImageElement>;
}

type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | null;

const HANDLE_SIZE = 10;
const HANDLE_OFFSET = 5;

export function useBannerInteraction(options: UseBannerInteractionOptions) {
  const {
    canvasRef,
    elements,
    selectedElementId,
    canvasWidth,
    canvasHeight,
    displayScale,
    onSelectElement,
    onUpdatePosition,
    onUpdateScale,
    onBringToFront,
    onDragStart,
    onDragEnd,
    onResizeStart,
    onResizeEnd,
    loadedImages,
  } = options;

  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [activeHandle, setActiveHandle] = useState<ResizeHandle>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [elementStartPos, setElementStartPos] = useState({ x: 0, y: 0 });
  const [elementStartScale, setElementStartScale] = useState(1);

  const clampPositionToCanvas = useCallback(
    (elementId: string, rawPosition: { x: number; y: number }) => {
      const element = elements.find((el) => el.id === elementId);
      if (!element) return rawPosition;

      const image = loadedImages.get(element.nftData.mintAddress);
      if (!image) return rawPosition;

      const halfWidth = (image.width * element.scale) / 2;
      const halfHeight = (image.height * element.scale) / 2;
      const minVisible = 24;

      const minX = -halfWidth + minVisible;
      const maxX = canvasWidth + halfWidth - minVisible;
      const minY = -halfHeight + minVisible;
      const maxY = canvasHeight + halfHeight - minVisible;

      return {
        x: minX <= maxX ? Math.max(minX, Math.min(maxX, rawPosition.x)) : canvasWidth / 2,
        y: minY <= maxY ? Math.max(minY, Math.min(maxY, rawPosition.y)) : canvasHeight / 2,
      };
    },
    [elements, loadedImages, canvasWidth, canvasHeight]
  );

  const screenToCanvas = useCallback(
    (screenX: number, screenY: number): { x: number; y: number } => {
      const canvas = canvasRef.current;
      if (!canvas || displayScale <= 0) return { x: 0, y: 0 };

      const rect = canvas.getBoundingClientRect();

      // Map screen coordinates to internal canvas coordinates using displayScale
      // internalCoord = (screenCoord - elementOffset) / (elementSize / internalSize)
      return {
        x: (screenX - rect.left) / displayScale,
        y: (screenY - rect.top) / displayScale,
      };
    },
    [canvasRef, displayScale]
  );

  const getElementBounds = useCallback(
    (element: BannerNFTElement) => {
      const image = loadedImages.get(element.nftData.mintAddress);
      if (!image) return null;

      // Consistency with BannerCanvas rendering logic
      const sw = Math.round(image.width * element.scale);
      const sh = Math.round(image.height * element.scale);
      const x = Math.round(element.position.x);
      const y = Math.round(element.position.y);
      const dx = Math.round(-sw / 2);
      const dy = Math.round(-sh / 2);

      const left = x + dx;
      const top = y + dy;

      return {
        left,
        top,
        right: left + sw,
        bottom: top + sh,
        width: sw,
        height: sh,
      };
    },
    [loadedImages]
  );

  const hitTestElement = useCallback(
    (canvasX: number, canvasY: number): string | null => {
      const sortedElements = [...elements].sort((a, b) => b.zIndex - a.zIndex);

      for (const element of sortedElements) {
        const bounds = getElementBounds(element);
        if (!bounds) continue;

        if (
          canvasX >= bounds.left &&
          canvasX <= bounds.right &&
          canvasY >= bounds.top &&
          canvasY <= bounds.bottom
        ) {
          return element.id;
        }
      }

      return null;
    },
    [elements, getElementBounds]
  );

  const hitTestResizeHandle = useCallback(
    (canvasX: number, canvasY: number): ResizeHandle => {
      if (!selectedElementId) return null;

      const element = elements.find((e) => e.id === selectedElementId);
      if (!element) return null;

      const bounds = getElementBounds(element);
      if (!bounds) return null;

      const centerX = Math.round(element.position.x);
      const centerY = Math.round(element.position.y);

      const handles: { handle: ResizeHandle; x: number; y: number }[] = [
        { handle: 'nw', x: bounds.left, y: bounds.top },
        { handle: 'n', x: centerX, y: bounds.top },
        { handle: 'ne', x: bounds.right, y: bounds.top },
        { handle: 'e', x: bounds.right, y: centerY },
        { handle: 'se', x: bounds.right, y: bounds.bottom },
        { handle: 's', x: centerX, y: bounds.bottom },
        { handle: 'sw', x: bounds.left, y: bounds.bottom },
        { handle: 'w', x: bounds.left, y: centerY },
      ];

      for (const { handle, x, y } of handles) {
        const dist = Math.sqrt((canvasX - x) ** 2 + (canvasY - y) ** 2);
        if (dist <= HANDLE_SIZE + HANDLE_OFFSET) {
          return handle;
        }
      }

      return null;
    },
    [elements, selectedElementId, getElementBounds]
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (e.button !== 0) return;

      const { x, y } = screenToCanvas(e.clientX, e.clientY);

      const resizeHandle = hitTestResizeHandle(x, y);
      if (resizeHandle) {
        const element = elements.find((el) => el.id === selectedElementId);
        if (element) {
          setIsResizing(true);
          setActiveHandle(resizeHandle);
          setDragStart({ x, y });
          setElementStartScale(element.scale);
          onResizeStart(resizeHandle);
        }
        return;
      }

      const hitElement = hitTestElement(x, y);
      if (hitElement) {
        onSelectElement(hitElement);
        onBringToFront(hitElement); // Bring selected element to front
        const element = elements.find((el) => el.id === hitElement);
        if (element) {
          setIsDragging(true);
          setDragStart({ x, y });
          setElementStartPos(element.position);
          onDragStart();
        }
      } else {
        onSelectElement(null);
      }
    },
    [
      screenToCanvas,
      hitTestResizeHandle,
      hitTestElement,
      elements,
      selectedElementId,
      onSelectElement,
      onBringToFront,
      onDragStart,
      onResizeStart,
    ]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const { x, y } = screenToCanvas(e.clientX, e.clientY);

      if (isDragging && selectedElementId) {
        const deltaX = x - dragStart.x;
        const deltaY = y - dragStart.y;
        const rawPosition = {
          x: elementStartPos.x + deltaX,
          y: elementStartPos.y + deltaY,
        };

        onUpdatePosition(selectedElementId, clampPositionToCanvas(selectedElementId, rawPosition));
      }

      if (isResizing && selectedElementId && activeHandle) {
        const element = elements.find((el) => el.id === selectedElementId);
        if (!element) return;

        const image = loadedImages.get(element.nftData.mintAddress);
        if (!image) return;

        const deltaX = x - dragStart.x;
        const deltaY = y - dragStart.y;

        let scaleDelta = 0;
        const baseSize = Math.max(image.width, image.height) * elementStartScale;

        switch (activeHandle) {
          case 'se':
          case 'ne':
          case 'sw':
          case 'nw':
            scaleDelta = (deltaX + deltaY) / baseSize;
            break;
          case 'e':
          case 'w':
            scaleDelta = deltaX / baseSize;
            break;
          case 'n':
          case 's':
            scaleDelta = deltaY / baseSize;
            break;
        }

        if (activeHandle.includes('w') || activeHandle === 'n') {
          scaleDelta = -scaleDelta;
        }

        const newScale = Math.max(0.1, Math.min(3, elementStartScale + scaleDelta));
        onUpdateScale(selectedElementId, newScale);
      }
    },
    [
      screenToCanvas,
      isDragging,
      isResizing,
      selectedElementId,
      activeHandle,
      dragStart,
      elementStartPos,
      elementStartScale,
      elements,
      loadedImages,
      onUpdatePosition,
      onUpdateScale,
      clampPositionToCanvas,
    ]
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      onDragEnd();
    }
    if (isResizing) {
      setIsResizing(false);
      setActiveHandle(null);
      onResizeEnd();
    }
  }, [isDragging, isResizing, onDragEnd, onResizeEnd]);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length !== 1) return;

      const touch = e.touches[0];
      const { x, y } = screenToCanvas(touch.clientX, touch.clientY);

      const resizeHandle = hitTestResizeHandle(x, y);
      if (resizeHandle) {
        const element = elements.find((el) => el.id === selectedElementId);
        if (element) {
          setIsResizing(true);
          setActiveHandle(resizeHandle);
          setDragStart({ x, y });
          setElementStartScale(element.scale);
          onResizeStart(resizeHandle);
        }
        return;
      }

      const hitElement = hitTestElement(x, y);
      if (hitElement) {
        onSelectElement(hitElement);
        onBringToFront(hitElement); // Bring selected element to front
        const element = elements.find((el) => el.id === hitElement);
        if (element) {
          setIsDragging(true);
          setDragStart({ x, y });
          setElementStartPos(element.position);
          onDragStart();
        }
      } else {
        onSelectElement(null);
      }
    },
    [
      screenToCanvas,
      hitTestResizeHandle,
      hitTestElement,
      elements,
      selectedElementId,
      onSelectElement,
      onBringToFront,
      onDragStart,
      onResizeStart,
    ]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length !== 1) return;

      const touch = e.touches[0];
      const { x, y } = screenToCanvas(touch.clientX, touch.clientY);

      if (isDragging && selectedElementId) {
        const deltaX = x - dragStart.x;
        const deltaY = y - dragStart.y;
        const rawPosition = {
          x: elementStartPos.x + deltaX,
          y: elementStartPos.y + deltaY,
        };

        onUpdatePosition(selectedElementId, clampPositionToCanvas(selectedElementId, rawPosition));
      }

      if (isResizing && selectedElementId && activeHandle) {
        const element = elements.find((el) => el.id === selectedElementId);
        if (!element) return;

        const image = loadedImages.get(element.nftData.mintAddress);
        if (!image) return;

        const deltaX = x - dragStart.x;
        const deltaY = y - dragStart.y;
        const baseSize = Math.max(image.width, image.height) * elementStartScale;

        let scaleDelta = (deltaX + deltaY) / baseSize;
        if (activeHandle.includes('w') || activeHandle === 'n') {
          scaleDelta = -scaleDelta;
        }

        const newScale = Math.max(0.1, Math.min(3, elementStartScale + scaleDelta));
        onUpdateScale(selectedElementId, newScale);
      }
    },
    [
      screenToCanvas,
      isDragging,
      isResizing,
      selectedElementId,
      activeHandle,
      dragStart,
      elementStartPos,
      elementStartScale,
      elements,
      loadedImages,
      onUpdatePosition,
      onUpdateScale,
      clampPositionToCanvas,
    ]
  );

  const handleTouchEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      onDragEnd();
    }
    if (isResizing) {
      setIsResizing(false);
      setActiveHandle(null);
      onResizeEnd();
    }
  }, [isDragging, isResizing, onDragEnd, onResizeEnd]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('touchstart', handleTouchStart, { passive: true });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: true });
    canvas.addEventListener('touchend', handleTouchEnd);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    canvasRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  ]);

  return {
    isDragging,
    isResizing,
    activeHandle,
    getElementBounds,
  };
}
